import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Send, ShieldCheck, AlertCircle } from 'lucide-react';
import { getContributorByToken, submitViaToken } from '../lib/api';
import { iceLogo } from '../mock';

export default function Contribute() {
  const { token } = useParams();
  const [contributor, setContributor] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ type: 'event', title: '', description: '', dateTime: '', location: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success

  useEffect(() => {
    getContributorByToken(token)
      .then(setContributor)
      .catch((e) => setError(e?.response?.data?.detail || 'This link is invalid or has been disabled by an admin.'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setStatus('submitting');
    try {
      await submitViaToken(token, form);
      setStatus('success');
      setForm({ type: 'event', title: '', description: '', dateTime: '', location: '' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Submission failed');
      setStatus('idle');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a1f44] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white/5 border border-white/10 p-8" data-testid="contribute-error">
          <AlertCircle className="w-10 h-10 text-[#c8102e] mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">Access Denied</div>
          <p className="text-white/70 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!contributor) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white/60" data-testid="contribute-loading">Verifying link…</div>;

  return (
    <div className="min-h-screen bg-[#f5f6f8] font-body py-16">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-8">
          <img src={iceLogo} alt="ICE" className="w-16 h-auto mx-auto mb-4" />
          {contributor.photoUrl && (
            <img
              src={contributor.photoUrl}
              alt={contributor.name}
              data-testid="contributor-photo"
              className="w-28 h-28 object-cover mx-auto mb-5 border-4 border-white shadow-xl"
            />
          )}
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-2 inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Authorized Contributor
          </div>
          <h1 className="font-display text-4xl text-[#0a1f44] leading-tight" data-testid="contributor-name">Welcome, {contributor.name}</h1>
          <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60 mt-1" data-testid="contributor-role">{contributor.role}</div>
        </div>

        {status === 'success' ? (
          <div className="bg-white shadow-2xl p-10 text-center" data-testid="contribute-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
            <div className="font-display text-2xl text-[#0a1f44] mb-2">SUBMITTED FOR REVIEW</div>
            <p className="text-[#0a1f44]/70 mb-6">Your submission is now in the admin queue and will appear on the Events page once approved.</p>
            <button onClick={() => setStatus('idle')} data-testid="contribute-submit-another" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-3">
              Add Another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white shadow-2xl p-8 space-y-4" data-testid="contribute-form">
            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-1">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="contribute-type" className="w-full border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]">
                <option value="event">Event</option>
                <option value="announcement">Announcement</option>
                <option value="happening">Program Happening</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-1">Title *</label>
              <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contribute-title" className="w-full border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" placeholder="e.g. Fall Kickoff Cookout" />
            </div>
            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-1">Details</label>
              <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="contribute-description" className="w-full border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" placeholder="Anything families should know…" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-1">Date &amp; Time</label>
                <input type="datetime-local" value={form.dateTime} onChange={(e) => setForm({ ...form, dateTime: e.target.value })} data-testid="contribute-datetime" className="w-full border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
              </div>
              <div>
                <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-1">Location</label>
                <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="contribute-location" placeholder="Field, address, or link" className="w-full border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
              </div>
            </div>
            <button type="submit" disabled={status === 'submitting'} data-testid="contribute-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-3.5 disabled:opacity-50">
              <Send className="w-4 h-4" /> {status === 'submitting' ? 'Sending…' : 'Submit for Review'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
